import { ConnectorRole, type ConnectorDefinition } from '../connectors/ConnectorDefinition';
import { forwardOf, rightOf, transformPoint, type Pose } from '../connectors/Pose';
import { wrapAngle, type Vec3 } from '../../utils/math';
import { EMPTY_SPAWN_PLAN, type SpawnPlan } from '../spawning/SpawnPlan';
import type { SegmentDefinition, SegmentTerrain, TrackArea } from './SegmentDefinition';
import { arcPoint, curvatureOf, pathToLocal, type PathPlacement, type PathPoint } from './segmentPath';

export type { PathPlacement, PathPoint } from './segmentPath';

/** World-space position + heading. */
export interface WorldFrame {
  x: number;
  y: number;
  z: number;
  yaw: number;
}

/**
 * A placed segment in the running track. Owns no visuals – presenters map
 * `uid` to their scene nodes.
 *
 * Path model: straight segments run along local +Z. Junction segments run
 * straight until the player actually turns; from then on the whole segment
 * is parameterised along the chosen exit leg through the pivot, which keeps
 * positions continuous for every lane and for the camera behind the player.
 * Curve segments follow a circular arc (segmentPath.ts) – no input needed.
 */
export class SegmentInstance {
  spawnPlan: SpawnPlan = EMPTY_SPAWN_PLAN;
  /** False for branch previews the player has not taken (yet). Uncommitted segments never collide. */
  committed = true;
  /** Exit the generated track continues through – null while a branch decision is open. */
  continuation: ConnectorDefinition | null;
  private pathExit: ConnectorDefinition | null;
  private readonly scratch: PathPoint = { x: 0, z: 0, yaw: 0 };
  /** Terrain as the run changed it (a pit filled by the dozer) – null: the definition's terrain. */
  private changedTerrain: SegmentTerrain | null = null;

  constructor(
    /** Unique per run, never reused. */
    readonly uid: number,
    /** Position in the generated sequence (0 = start segment). */
    readonly sequenceIndex: number,
    readonly definition: SegmentDefinition,
    /** World pose of the segment origin (= its CONNECTOR_IN). */
    readonly pose: Pose,
    /** Track distance at the segment entry. */
    readonly startDistance: number,
  ) {
    const exits = definition.exits;
    this.continuation = exits.length === 1 ? (exits[0] ?? null) : null;
    this.pathExit = definition.junction ? null : (exits[0] ?? null);
  }

  /** Walkable geometry of this placement: the definition's terrain, as the run may have changed it (fill). */
  get terrain(): SegmentTerrain | undefined {
    return this.changedTerrain ?? this.definition.terrain;
  }

  /**
   * Fills an area with earth up to `height` (the dozer pushed its pile into the pit): a surface of its own, which wins
   * over the gap below it (terrainQueries) – the area is walkable from now on, for this placement only.
   */
  fill(id: string, area: TrackArea, height: number): void {
    const base = this.terrain ?? { surfaces: [], gaps: [], ceilings: [] };
    this.changedTerrain = { ...base, surfaces: [...base.surfaces, { id, ...area, heightFrom: height, heightTo: height }] };
  }

  get endDistance(): number {
    return this.startDistance + this.definition.length;
  }

  get isJunction(): boolean {
    return this.definition.junction !== undefined;
  }

  /** Junction whose turn has been executed (or straight continuation chosen). */
  get isResolved(): boolean {
    return this.pathExit !== null;
  }

  get resolvedExit(): ConnectorDefinition | null {
    return this.pathExit;
  }

  /** Track distance of the junction pivot (junction segments only). */
  get pivotDistance(): number | null {
    const pivot = this.definition.junction?.pivot;
    return pivot ? this.startDistance + pivot.z : null;
  }

  /** Fixes the path through a junction (called by the TurnController). */
  resolve(exit: ConnectorDefinition): void {
    this.pathExit = exit;
    this.continuation = exit;
  }

  containsDistance(distance: number): boolean {
    return distance >= this.startDistance && distance < this.endDistance;
  }

  /** Track distance → path parameter (meters from the segment entry). */
  localZ(distance: number): number {
    return distance - this.startDistance;
  }

  /** Centerline point at path parameter s in segment-local space. */
  pathPoint(s: number, out: PathPoint = { x: 0, z: 0, yaw: 0 }): PathPoint {
    const curvature = curvatureOf(this.definition);
    if (curvature !== 0) return arcPoint(curvature, s, out);
    const pivot = this.definition.junction?.pivot;
    const exit = this.pathExit;
    if (!pivot || !exit || exit.role === ConnectorRole.Out) {
      out.x = 0;
      out.z = s;
      out.yaw = 0;
      return out;
    }
    const forward = forwardOf(exit.transform.yaw);
    out.x = pivot.x + forward.x * (s - pivot.z);
    out.z = pivot.z + forward.z * (s - pivot.z);
    out.yaw = exit.transform.yaw;
    return out;
  }

  /** Track space (lateral x, height y, track distance) → world position + heading. */
  trackToWorld(x: number, y: number, distance: number, out: WorldFrame): WorldFrame {
    const p = this.pathPoint(this.localZ(distance), this.scratch);
    const right = rightOf(p.yaw);
    const world = transformPoint(this.pose, { x: p.x + right.x * x, y, z: p.z + right.z * x });
    out.x = world.x;
    out.y = world.y;
    out.z = world.z;
    out.yaw = wrapAngle(this.pose.yaw + p.yaw);
    return out;
  }

  /**
   * Socket position (path coordinates) → segment-local placement for its visual: identity on
   * straight modules, on the arc for curves (heading = path tangent). Parent the visual to the
   * segment root, set this position and add `yaw` to the visual's own rotation.
   */
  placement(local: Readonly<Vec3>, out?: PathPlacement): PathPlacement {
    return pathToLocal(this.definition, local, out);
  }

  /**
   * Segment-local socket position → track space. Valid for sockets on the
   * straight part (all spawn points lie on the approach of junctions) and for
   * curves, whose socket data is given in path coordinates.
   */
  localToTrack(local: Readonly<Vec3>, out: Vec3 = { x: 0, y: 0, z: 0 }): Vec3 {
    out.x = local.x;
    out.y = local.y;
    out.z = this.startDistance + local.z;
    return out;
  }

  /** Segment-local point → world space. */
  localToWorld(local: Readonly<Vec3>, out?: Vec3): Vec3 {
    return transformPoint(this.pose, local, out);
  }
}
